<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

const props = defineProps({
  order: {
    type: Object,
    required: true,
  },
  isUpdatingStatus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['updateOrderStatus']);
const { t } = useI18n();

const firstPresent = (...values) =>
  values.find(value => value !== undefined && value !== null && value !== '');

const readPath = (object, path) => {
  if (!object) return undefined;
  return path.split('.').reduce((currentValue, key) => {
    if (currentValue === undefined || currentValue === null) return undefined;
    return currentValue[key];
  }, object);
};

const normalizePaymentMethod = type => {
  const normalized = String(type || '')
    .replace(/([a-z])([A-Z])/g, '$1_$2')
    .toLowerCase();

  if (normalized === 'pix' || normalized === 'pix_dynamic_code') {
    return 'pix_dynamic_code';
  }
  if (normalized === 'boleto') return 'boleto';
  if (normalized === 'link' || normalized === 'payment_link') {
    return 'payment_link';
  }
  return normalized;
};

const formatCurrency = amount => {
  if (!amount) return 'R$ 0,00';
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(amount / 100);
};

const paymentSetting = computed(() => {
  const settings = props.order?.payment_settings || [];
  return settings[0] || {};
});

const paymentMethod = computed(() => {
  const methodFromSettings = normalizePaymentMethod(paymentSetting.value.type);
  if (methodFromSettings) return methodFromSettings;

  return normalizePaymentMethod(props.order?.payment_methods?.[0]);
});

const paymentMethodLabel = computed(() => {
  const labels = {
    pix_dynamic_code: t('CONVERSATION.WHATSAPP_TEMPLATE.PIX_COPY_PASTE'),
    boleto: t('CONVERSATION.WHATSAPP_TEMPLATE.BOLETO'),
    payment_link: t('CONVERSATION.WHATSAPP_TEMPLATE.PAYMENT_LINK'),
  };
  return labels[paymentMethod.value] || paymentMethod.value;
});

const paymentMethodIcon = computed(() => {
  const icons = {
    pix_dynamic_code: 'i-lucide-qr-code',
    boleto: 'i-lucide-barcode',
    payment_link: 'i-lucide-external-link',
  };
  return icons[paymentMethod.value] || 'i-lucide-credit-card';
});

const paymentActionValue = computed(() => {
  const setting = paymentSetting.value;

  if (paymentMethod.value === 'pix_dynamic_code') {
    return firstPresent(
      readPath(setting, 'pix_dynamic_code.code'),
      readPath(setting, 'pixDynamicCode.code'),
      setting.pix_code,
      setting.pixCode,
      setting.code
    );
  }

  if (paymentMethod.value === 'boleto') {
    return firstPresent(
      readPath(setting, 'boleto.digitable_line'),
      readPath(setting, 'boleto.digitableLine'),
      setting.boleto_digitable_line,
      setting.boletoDigitableLine,
      setting.code
    );
  }

  if (paymentMethod.value === 'payment_link') {
    return firstPresent(
      readPath(setting, 'payment_link.uri'),
      readPath(setting, 'paymentLink.uri'),
      setting.payment_link_uri,
      setting.paymentLinkUri,
      setting.uri
    );
  }

  return null;
});

const paymentActionLabel = computed(() => {
  const labels = {
    pix_dynamic_code: t('CONVERSATION.WHATSAPP_TEMPLATE.COPY_PIX_CODE'),
    boleto: t('CONVERSATION.WHATSAPP_TEMPLATE.COPY_BOLETO_CODE'),
    payment_link: t('CONVERSATION.WHATSAPP_TEMPLATE.OPEN_PAYMENT_LINK'),
  };
  return labels[paymentMethod.value] || '';
});

const orderStatus = computed(() => props.order?.order_status || 'pending');
const paymentStatus = computed(() => props.order?.payment_status || 'pending');

const orderStatusMeta = computed(() => {
  const statuses = {
    pending: {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_PENDING'),
      className: 'bg-n-amber-9/10 text-n-amber-11 border border-n-amber-9/20',
      icon: null,
    },
    processing: {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_PROCESSING'),
      className: 'bg-n-blue-9/10 text-n-blue-11 border border-n-blue-9/20',
      icon: 'i-lucide-loader',
    },
    'partially-shipped': {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_PARTIALLY_SHIPPED'),
      className: 'bg-n-blue-9/10 text-n-blue-11 border border-n-blue-9/20',
      icon: 'i-lucide-package-check',
    },
    shipped: {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_SHIPPED'),
      className: 'bg-n-blue-9/10 text-n-blue-11 border border-n-blue-9/20',
      icon: 'i-lucide-truck',
    },
    completed: {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_COMPLETED'),
      className: 'bg-n-green-9/10 text-n-green-11 border border-n-green-9/20',
      icon: 'i-lucide-check',
    },
    canceled: {
      label: t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_CANCELED'),
      className: 'bg-n-ruby-9/10 text-n-ruby-11 border border-n-ruby-9/20',
      icon: 'i-lucide-x',
    },
  };
  return statuses[orderStatus.value] || statuses.pending;
});

const paymentStatusLabel = computed(() => {
  const labels = {
    pending: t('CONVERSATION.WHATSAPP_TEMPLATE.PAYMENT_PENDING'),
    captured: t('CONVERSATION.WHATSAPP_TEMPLATE.PAYMENT_CAPTURED'),
    failed: t('CONVERSATION.WHATSAPP_TEMPLATE.PAYMENT_FAILED'),
  };
  return labels[paymentStatus.value] || paymentStatus.value;
});

const canUpdateOrderStatus = computed(() => {
  return ['pending', 'processing'].includes(orderStatus.value);
});

const handlePaymentAction = async () => {
  if (!paymentActionValue.value) return;

  if (paymentMethod.value === 'payment_link') {
    window.open(paymentActionValue.value, '_blank', 'noopener,noreferrer');
    return;
  }

  try {
    await navigator.clipboard.writeText(paymentActionValue.value);
    if (paymentMethod.value === 'boleto') {
      useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.BOLETO_COPIED'));
    } else {
      useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.PIX_COPIED'));
    }
  } catch {
    useAlert(t('CONVERSATION.WHATSAPP_TEMPLATE.COPY_ERROR'));
  }
};

const updateOrderStatus = (orderStatusValue, paymentStatusValue = null) => {
  emit('updateOrderStatus', orderStatusValue, paymentStatusValue);
};
</script>

<template>
  <div
    class="mt-3 p-3 bg-white dark:bg-n-solid-3 border border-n-weak rounded-xl shadow-sm space-y-3"
    data-test-id="whatsapp-payment-order-card"
  >
    <div class="flex items-center justify-between pb-2 border-b border-n-weak">
      <div
        class="flex items-center gap-1.5 text-sm font-semibold text-n-slate-12"
      >
        <span class="i-lucide-receipt size-4 text-n-slate-11" />
        {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_DETAILS') }}
      </div>
      <span
        class="px-2 py-0.5 text-[10px] font-medium rounded-full uppercase tracking-wide flex items-center gap-1"
        :class="orderStatusMeta.className"
        data-test-id="whatsapp-order-status"
      >
        <span
          v-if="orderStatusMeta.icon"
          :class="orderStatusMeta.icon"
          class="size-3"
        />
        {{ orderStatusMeta.label }}
      </span>
    </div>

    <div class="grid grid-cols-2 gap-y-2 gap-x-4 text-xs">
      <div>
        <span class="block text-n-slate-10 mb-0.5">
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_ID') }}
        </span>
        <span
          class="font-medium text-n-slate-12 truncate block"
          :title="order.reference_id"
        >
          {{ order.reference_id || '-' }}
        </span>
      </div>
      <div>
        <span class="block text-n-slate-10 mb-0.5">
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_TOTAL') }}
        </span>
        <span class="font-semibold text-n-slate-12">
          {{ formatCurrency(order.total_amount) }}
        </span>
      </div>
      <div class="col-span-2">
        <span class="block text-n-slate-10 mb-0.5">
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_METHOD') }}
        </span>
        <div class="flex items-center gap-1.5 font-medium text-n-slate-12">
          <span :class="paymentMethodIcon" class="size-3.5 text-n-slate-11" />
          {{ paymentMethodLabel }}
        </div>
      </div>
      <div class="col-span-2">
        <span class="block text-n-slate-10 mb-0.5">
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.PAYMENT_STATUS') }}
        </span>
        <span
          class="font-medium text-n-slate-12"
          data-test-id="whatsapp-payment-status"
        >
          {{ paymentStatusLabel }}
        </span>
      </div>
    </div>

    <div class="pt-2 flex flex-col gap-1.5">
      <button
        v-if="paymentActionValue"
        class="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-medium text-n-slate-12 bg-n-alpha-1 hover:bg-n-alpha-2 rounded-lg transition-colors border border-n-weak"
        data-test-id="whatsapp-payment-action"
        @click="handlePaymentAction"
      >
        <span
          :class="
            paymentMethod === 'payment_link'
              ? 'i-lucide-external-link'
              : 'i-lucide-copy'
          "
          class="size-3.5 text-n-slate-11"
        />
        {{ paymentActionLabel }}
      </button>

      <div v-if="canUpdateOrderStatus" class="grid grid-cols-2 gap-1.5">
        <button
          class="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-n-ruby-11 bg-n-ruby-9/10 hover:bg-n-ruby-9/20 rounded-lg transition-colors"
          :disabled="isUpdatingStatus"
          @click="updateOrderStatus('canceled')"
        >
          <span
            v-if="isUpdatingStatus"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-x size-3.5" />
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_CANCEL_BTN') }}
        </button>
        <button
          class="flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-n-green-11 bg-n-green-9/10 hover:bg-n-green-9/20 rounded-lg transition-colors"
          :disabled="isUpdatingStatus"
          @click="updateOrderStatus('completed', 'captured')"
        >
          <span
            v-if="isUpdatingStatus"
            class="i-lucide-loader-2 size-3.5 animate-spin"
          />
          <span v-else class="i-lucide-check size-3.5" />
          {{ t('CONVERSATION.WHATSAPP_TEMPLATE.ORDER_PAID_BTN') }}
        </button>
      </div>
    </div>
  </div>
</template>
